import { useEffect, useRef, type ReactNode } from "react";

import type { AlphaAiSummary } from "../alpha-session-controller";
import { AlphaPhaseRail } from "../tutorial/AlphaPhaseRail";

export function AlphaGameplayMenu({
  phase,
  aiSummaries,
  canExportReplay,
  isAiTurn,
  paused,
  quickAi,
  reducedMotion,
  onClose,
  onHowToPlay,
  onExportReplay,
  onPause,
  onFastAi,
  onResetTableView,
  onNewMatch,
  onDeveloperMode,
  children,
}: {
  readonly phase: Parameters<typeof AlphaPhaseRail>[0]["phase"];
  readonly aiSummaries: readonly AlphaAiSummary[];
  readonly canExportReplay: boolean;
  readonly isAiTurn: boolean;
  readonly paused: boolean;
  readonly quickAi: boolean;
  readonly reducedMotion: boolean;
  readonly onClose: () => void;
  readonly onHowToPlay: () => void;
  readonly onExportReplay: () => void;
  readonly onPause: () => void;
  readonly onFastAi: () => void;
  readonly onResetTableView: () => void;
  readonly onNewMatch: () => void;
  readonly onDeveloperMode: () => void;
  readonly children?: ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      if (event.key !== "Tab") return;
      const drawer = closeRef.current?.closest('[role="dialog"]');
      const focusable = drawer?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable === undefined || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="alpha-menu-scrim"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="alpha-gameplay-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alpha-gameplay-menu-title"
      >
        <header>
          <span>
            <small>War Table</small>
            <h2 id="alpha-gameplay-menu-title">Game menu</h2>
          </span>
          <button ref={closeRef} type="button" onClick={onClose}>
            Close
          </button>
        </header>
        <section>
          <h3>Turn</h3>
          <AlphaPhaseRail phase={phase} />
        </section>
        {isAiTurn ? (
          <section>
            <h3>Opponent turn</h3>
            <div className="alpha-gameplay-menu__row">
              <button type="button" onClick={onPause}>
                {paused ? "Resume AI" : "Pause AI"}
              </button>
              <button type="button" onClick={onFastAi} disabled={quickAi || paused}>
                Fast AI
              </button>
            </div>
          </section>
        ) : null}
        <section>
          <h3>Recent actions</h3>
          {aiSummaries.length === 0 ? (
            <p>No opponent actions recorded yet.</p>
          ) : (
            <ol aria-label="Recent AI actions">
              {aiSummaries.slice(-6).map((summary) => (
                <li key={`${summary.actionNumber}-${summary.summary}`}>{summary.summary}</li>
              ))}
            </ol>
          )}
        </section>
        <section className="alpha-gameplay-menu__actions">
          <button type="button" onClick={onHowToPlay}>
            How to Play
          </button>
          <button type="button" onClick={onResetTableView}>
            Reset table view
          </button>
          <button type="button" onClick={onExportReplay} disabled={!canExportReplay}>
            Export verified replay
          </button>
          <button type="button" onClick={onNewMatch}>
            New match
          </button>
          <button type="button" onClick={onDeveloperMode}>
            Developer Mode
          </button>
        </section>
        {children}
        <p className="alpha-gameplay-menu__setting">
          Motion preference: <b>{reducedMotion ? "Reduced" : "Standard"}</b>
        </p>
      </aside>
    </div>
  );
}
